import { useCallback, useEffect, useState } from 'react';

import { useTranslations } from 'next-intl';

import { ChatMessage } from '@repo/contracts';

export type ChatMessageWithId = ChatMessage & { _id: string };

function withId(msg: ChatMessage): ChatMessageWithId {
    return { ...msg, _id: crypto.randomUUID() };
}

export interface ChatConversation {
    id: string;
    title: string;
    messages: ChatMessageWithId[];
    updatedAt: number;
}

export interface UseChatMessagesReturn {
    conversations: ChatConversation[];
    activeId: string | null;
    input: string;
    setInput: (value: string) => void;
    
    // Actions on conversations
    createConversation: () => void;
    switchConversation: (id: string) => void;
    deleteConversation: (id: string) => void;
    restoreConversations: (restored: ChatConversation[], restoredActiveId: string | null) => void;
    renameActiveConversation: (title: string) => void;
    renameConversation: (id: string, title: string) => void;

    // Actions on active conversation
    activeMessages: ChatMessageWithId[];
    appendMessage: (message: ChatMessage) => void;
    appendMessages: (newMessages: ChatMessage[]) => void;
    resetMessages: () => void; // Used to clear active conversation
    updateLastAssistantMessage: (content: string) => void;
    sliceMessagesAtLastUser: () => ChatMessageWithId[];
}

export function useChatMessages(): UseChatMessagesReturn {
    const t = useTranslations();
    const [conversations, setConversations] = useState<ChatConversation[]>([]);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [input, setInput] = useState('');

    const activeMessages = conversations.find((c) => c.id === activeId)?.messages ?? [];

    useEffect(() => {
        if (activeId) {
            localStorage.setItem('chatbot_active_conversation_id', activeId);
        }
    }, [activeId]);

    const createConversation = useCallback(() => {
        const newConv: ChatConversation = {
            id: crypto.randomUUID(),
            title: t('new'),
            messages: [],
            updatedAt: Date.now(),
        };
        setConversations((prev) => [...prev, newConv]);
        setActiveId(newConv.id);
    }, []);

    const switchConversation = useCallback((id: string) => {
        setActiveId(id);
    }, []);

    const deleteConversation = useCallback((id: string) => {
        let activeWasDeleted = false;
        setActiveId((prev) => {
            if (prev === id) {
                activeWasDeleted = true;
                return null; // Temporarily null, handled in setConversations
            }
            return prev;
        });

        setConversations((prev) => {
            const next = prev.filter((c) => c.id !== id);
            if (next.length === 0) {
                const newConv: ChatConversation = {
                    id: crypto.randomUUID(),
                    title: t('new'),
                    messages: [],
                    updatedAt: Date.now(),
                };
                setActiveId(newConv.id);
                return [newConv];
            }
            if (activeWasDeleted) {
                const fallbackId = next[next.length - 1]?.id;
                if (fallbackId) setActiveId(fallbackId);
            }
            return next;
        });
    }, []);

    const restoreConversations = useCallback((restored: ChatConversation[], restoredActiveId: string | null) => {
        if (restored.length > 0) {
            setConversations(restored);
            const fallbackId = restoredActiveId ?? restored[0]?.id;
            if (fallbackId) setActiveId(fallbackId);
        } else {
            createConversation();
        }
    }, [createConversation]);

    const renameActiveConversation = useCallback((title: string) => {
        setConversations((prev) =>
            prev.map((c) => (c.id === activeId ? { ...c, title, updatedAt: Date.now() } : c)),
        );
    }, [activeId]);

    const renameConversation = useCallback((id: string, title: string) => {
        setConversations((prev) =>
            prev.map((c) => (c.id === id ? { ...c, title, updatedAt: Date.now() } : c)),
        );
    }, []);

    const appendMessage = useCallback((message: ChatMessage) => {
        setConversations((prev) =>
            prev.map((c) =>
                c.id === activeId
                    ? { ...c, messages: [...c.messages, withId(message)], updatedAt: Date.now() }
                    : c,
            ),
        );
    }, [activeId]);

    const appendMessages = useCallback((newMessages: ChatMessage[]) => {
        setConversations((prev) =>
            prev.map((c) =>
                c.id === activeId
                    ? { ...c, messages: [...c.messages, ...newMessages.map(withId)], updatedAt: Date.now() }
                    : c,
            ),
        );
    }, [activeId]);

    const resetMessages = useCallback(() => {
        setConversations((prev) =>
            prev.map((c) => (c.id === activeId ? { ...c, messages: [], updatedAt: Date.now(), title: t('new') } : c)),
        );
    }, [activeId]);

    const updateLastAssistantMessage = useCallback((content: string) => {
        setConversations((prev) =>
            prev.map((c) => {
                if (c.id !== activeId) return c;
                const lastIndex = c.messages.length - 1;
                if (lastIndex < 0 || c.messages[lastIndex]?.role !== 'assistant') return c;
                const nextMessages = [...c.messages];
                nextMessages[lastIndex] = { ...nextMessages[lastIndex]!, content };
                return { ...c, messages: nextMessages };
            }),
        );
    }, [activeId]);

    const sliceMessagesAtLastUser = useCallback(() => {
        let truncated: ChatMessageWithId[] = [];
        setConversations((prev) =>
            prev.map((c) => {
                if (c.id !== activeId) return c;
                const lastUserIndex = c.messages.map((m) => m.role).lastIndexOf('user');
                if (lastUserIndex === -1) {
                    truncated = c.messages;
                    return c;
                }
                truncated = c.messages.slice(0, lastUserIndex);
                return { ...c, messages: truncated };
            }),
        );
        return truncated;
    }, [activeId]);

    return {
        conversations,
        activeId,
        input,
        setInput,
        createConversation,
        switchConversation,
        deleteConversation,
        restoreConversations,
        renameActiveConversation,
        renameConversation,
        activeMessages,
        appendMessage,
        appendMessages,
        resetMessages,
        updateLastAssistantMessage,
        sliceMessagesAtLastUser,
    };
}